import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AiChatSessionService } from "../../../../services/aiChatSessionService";
import IGlobalConfig from "../../../../types/IGlobalConfig";
import { AiChatMessage } from "../../../../types/services/IAiChatSession";

function chatErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }
  const message = error.message.trim();
  if (message.includes("NOT_CHARITY_USER")) {
    return message.replace(/^NOT_CHARITY_USER\s*\/\s*/i, "").trim() || fallback;
  }
  return message || fallback;
}

interface UseChatSessionThreadParams {
  config: IGlobalConfig;
  serviceAccessToken: string;
  charityId?: string;
  sessionId?: string;
  isDraft?: boolean;
  onSessionCreated?: () => Promise<void> | void;
}

interface UseChatSessionThreadReturn {
  messages: AiChatMessage[];
  message: string;
  loading: boolean;
  error: string | null;
  sendLoading: boolean;
  sendError: string | null;
  handleMessageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (event: FormEvent) => void;
}

export function useChatSessionThread({
  config,
  serviceAccessToken,
  charityId,
  sessionId,
  isDraft = false,
  onSessionCreated,
}: UseChatSessionThreadParams): UseChatSessionThreadReturn {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const submitGenerationRef = useRef(0);

  // Invalidate in-flight submit when leaving the current thread (or unmounting).
  useEffect(() => {
    return () => {
      submitGenerationRef.current += 1;
    };
  }, [sessionId, isDraft]);

  useEffect(() => {
    if (!sessionId || isDraft) {
      setMessages([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (!charityId) {
      setMessages([]);
      setError("Select a charity to load this conversation.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadThread = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await AiChatSessionService.getSession(
          config,
          serviceAccessToken,
          charityId,
          sessionId
        );
        if (!cancelled) {
          setMessages(result.messages);
        }
      } catch (err) {
        if (!cancelled) {
          setMessages([]);
          setError(chatErrorMessage(err, "Failed to load this conversation."));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadThread();

    return () => {
      cancelled = true;
    };
  }, [charityId, config, isDraft, serviceAccessToken, sessionId]);

  const handleMessageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setMessage(event.target.value);
      setSendError(currentError => (currentError ? null : currentError));
    },
    []
  );

  const submit = useCallback(async () => {
    const trimmed = message.trim();
    if (!trimmed || sendLoading) {
      return;
    }

    if (!charityId) {
      setSendError(
        isDraft
          ? "Select a charity to start a conversation."
          : "Select a charity to send a message."
      );
      return;
    }

    if (!sessionId) {
      return;
    }

    const generation = submitGenerationRef.current;
    setSendLoading(true);
    setSendError(null);

    const optimisticUserMessage: AiChatMessage = {
      id: `pending-user-${Date.now()}`,
      role: "user",
      content: trimmed,
      createdAt: new Date().toISOString(),
    };

    setMessage("");
    setMessages(current => [...current, optimisticUserMessage]);

    try {
      if (isDraft) {
        const result = await AiChatSessionService.createSession(
          config,
          serviceAccessToken,
          charityId,
          { message: trimmed }
        );
        // Session exists on the server; refresh the list even if the user left.
        await onSessionCreated?.();
        if (generation !== submitGenerationRef.current) {
          return;
        }
        navigate(`/chat-session/${result.sessionId}`);
        return;
      }

      const result = await AiChatSessionService.sendMessage(
        config,
        serviceAccessToken,
        charityId,
        sessionId,
        { message: trimmed }
      );
      if (generation !== submitGenerationRef.current) {
        return;
      }
      setMessages(current => [...current, result.assistantMessage]);
    } catch (err) {
      if (generation !== submitGenerationRef.current) {
        return;
      }
      setMessages(current =>
        current.filter(item => item.id !== optimisticUserMessage.id)
      );
      setMessage(trimmed);
      setSendError(
        chatErrorMessage(
          err,
          isDraft
            ? "Failed to start chat. Please try again."
            : "Failed to send message. Please try again."
        )
      );
    } finally {
      setSendLoading(false);
    }
  }, [
    charityId,
    config,
    isDraft,
    message,
    navigate,
    onSessionCreated,
    sendLoading,
    serviceAccessToken,
    sessionId,
  ]);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      submit();
    },
    [submit]
  );

  return {
    messages,
    message,
    loading,
    error,
    sendLoading,
    sendError,
    handleMessageChange,
    handleSubmit,
  };
}
